import { useState } from "react";
import { timeAgo, initials } from "@/lib/format";
import {
  PaperPlaneTilt, LockKey, ChatCircle, EnvelopeSimple, ArrowBendUpLeft, PencilSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AttachmentList } from "./AttachmentList";

export function MessageCard({ variant, authorName, timestamp, subtitleExtra, body, testid, attachments, portalToken, onEdit }) {
  // variant: "email_in" | "email_out" | "portal" | "internal" | "reply" | "original"
  const config = {
    email_in: {
      accent: "border-l-4 border-l-[#4f4c90]/70",
      bg: "bg-[#EEF1F8]",
      chipCls: "!text-[#8B87C8] !border-[#4f4c90]/40 bg-[#4f4c90]/10",
      chipIcon: <EnvelopeSimple size={10} weight="fill" />,
      chipLabel: "Email received",
    },
    email_out: {
      accent: "border-l-4 border-l-[#39A36E]/70",
      bg: "bg-[#E9F5EE]",
      chipCls: "!text-[#39A36E] !border-[#39A36E]/40 bg-[#39A36E]/10",
      chipIcon: <PaperPlaneTilt size={10} weight="fill" />,
      chipLabel: "Email sent",
    },
    portal: {
      accent: "border-l-4 border-l-[#F59E0B]/70",
      bg: "bg-[#FEF3C7]",
      chipCls: "!text-[#F59E0B] !border-[#F59E0B]/40 bg-[#F59E0B]/10",
      chipIcon: <ArrowBendUpLeft size={10} weight="fill" />,
      chipLabel: "Portal reply",
    },
    internal: {
      accent: "border-l-4 border-l-yellow-500/60",
      bg: "bg-[#FEF9C3]",
      chipCls: "!text-yellow-400 !border-yellow-500/30 bg-yellow-500/10",
      chipIcon: <LockKey size={10} weight="fill" />,
      chipLabel: "Internal note",
    },
    reply: {
      accent: "border-l-4 border-l-white/15",
      bg: "bg-white",
      chipCls: "!text-slate-600 !border-slate-300 bg-slate-100",
      chipIcon: <ChatCircle size={10} weight="fill" />,
      chipLabel: "Reply",
    },
    original: {
      accent: "border-l-4 border-l-[#4f4c90]/70",
      bg: "bg-[#EEF1F8]",
      chipCls: "!text-[#8B87C8] !border-[#4f4c90]/40 bg-[#4f4c90]/10",
      chipIcon: <EnvelopeSimple size={10} weight="fill" />,
      chipLabel: "Original email",
    },
  }[variant] || {
    accent: "border-l-4 border-l-white/10",
    bg: "bg-white",
    chipCls: "!text-slate-600 !border-slate-300 bg-slate-100",
    chipIcon: null,
    chipLabel: variant,
  };

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body || "");

  return (
    <div data-testid={testid} className={`hairline rounded-lg p-4 ${config.bg} ${config.accent}`}>
      <div className="flex items-start gap-3 mb-2">
        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-[10px] shrink-0">
          {initials(authorName)}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-sm font-medium truncate">{authorName}</div>
            <span className={`chip gap-1 ${config.chipCls}`}>{config.chipIcon} {config.chipLabel}</span>
            {onEdit && !editing && (
              <button
                onClick={() => { setDraft(body || ""); setEditing(true); }}
                className="ml-auto p-1 text-slate-400 hover:text-[#4f4c90] transition"
                title="Edit"
                data-testid={testid ? `${testid}-edit` : undefined}
              >
                <PencilSimple size={12} />
              </button>
            )}
          </div>
          <div className="text-[11px] text-slate-500 font-mono mt-0.5">
            {timeAgo(timestamp)}
            {subtitleExtra ? <span className="text-slate-400"> · {subtitleExtra}</span> : null}
          </div>
        </div>
      </div>
      {editing ? (
        <div>
          <Textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className="bg-white border-slate-200 min-h-32 text-sm"
            data-testid={testid ? `${testid}-edit-input` : undefined}
          />
          <div className="flex gap-1 mt-2">
            <Button
              onClick={async () => { await onEdit(draft); setEditing(false); }}
              className="bg-[#4f4c90] hover:bg-[#5F5CA5] h-7 text-xs"
              data-testid={testid ? `${testid}-edit-save` : undefined}
            >Save</Button>
            <Button onClick={() => setEditing(false)} variant="ghost" className="h-7 text-xs text-slate-600">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{body}</div>
      )}
      <AttachmentList attachments={attachments} portalToken={portalToken} />
    </div>
  );
}
